import { BrainCircuit, CloudCog, ShieldCheck, Network, Database, Code2, Check } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Reveal, Eyebrow } from "@/components/Reveal";

const SERVICES = [
    {
        icon: BrainCircuit,
        title: "AI & Machine Learning",
        body: "Production-grade AI, from copilots to autonomous agents.",
        points: ["Agentic AI workflows & copilots", "LLM fine-tuning on private data", "Speech, vision & predictive systems", "MLOps & model governance"],
    },
    {
        icon: CloudCog,
        title: "Cloud Transformation",
        body: "Hybrid and multi-cloud foundations that scale without surprises.",
        points: ["Landing zones on AWS, Azure & GCP", "Kubernetes & container platforms", "Migration & modernization", "FinOps cost optimization"],
    },
    {
        icon: ShieldCheck,
        title: "Cybersecurity",
        body: "Zero-trust security woven into every layer we ship.",
        points: ["Zero-trust architecture", "SOC & threat monitoring", "VAPT & compliance audits", "ICS/SCADA security"],
    },
    {
        icon: Network,
        title: "System Integration",
        body: "Your estate, finally speaking one language.",
        points: ["API & event-driven integration", "ERP / CRM connectivity", "Legacy modernization", "Kafka-based streaming backbones"],
    },
    {
        icon: Database,
        title: "Data Engineering & Analytics",
        body: "Trusted data pipelines that feed decisions and models alike.",
        points: ["Lakehouse & CDP platforms", "Real-time streaming pipelines", "BI dashboards & governance", "Data quality & lineage"],
    },
    {
        icon: Code2,
        title: "Application Development",
        body: "Products your users love, engineered to enterprise standards.",
        points: ["Web & mobile product engineering", "Design systems & UX", "API-first backends", "QA automation & DevOps"],
    },
];

const PROCESS = [
    { step: "01", title: "Discover", body: "Workshops, audits and data exploration to find the highest-leverage starting point." },
    { step: "02", title: "Design", body: "Architecture, security model and delivery plan, reviewed with your team, not handed down." },
    { step: "03", title: "Deliver", body: "Agile builds with weekly demos, hardened for production and compliance from day one." },
    { step: "04", title: "Scale", body: "SLA-backed support, training and continuous improvement long after go-live." },
];

export default function ServicesPage() {
    return (
        <PageShell
            eyebrow="Our Services"
            title="From strategy to production,"
            accent="one accountable team."
            sub="Six practices, one integrated delivery engine. Every engagement is tailored to your business goals, compliance landscape and scale."
            testid="services-page"
        >
            <section className="py-20 sm:py-28">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.title} delay={(i % 3) * 0.1}>
                                <div data-testid={`service-card-${i}`} className="card-glow-border group flex h-full flex-col rounded-2xl border border-white/[0.08] bg-card2/50 p-8">
                                    <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-volt/20 bg-volt/10 text-volt transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_0_25px_rgba(0,240,255,0.3)]">
                                        <s.icon size={22} />
                                    </div>
                                    <h3 className="mb-2 font-syne text-xl font-semibold text-white">{s.title}</h3>
                                    <p className="mb-5 text-sm text-slate-500">{s.body}</p>
                                    <ul className="mt-auto space-y-2.5">
                                        {s.points.map((p) => (
                                            <li key={p} className="flex items-start gap-2.5 text-sm text-slate-300">
                                                <span className="mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-volt/15 p-0.5">
                                                    <Check size={11} className="text-volt" />
                                                </span>
                                                {p}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-white/[0.06] bg-panel/40 py-20 sm:py-24" data-testid="services-process">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <Eyebrow>How We Deliver</Eyebrow>
                        <h2 className="mb-4 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="services-process-heading">
                            A process built for long quantum-safe migrations
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {PROCESS.map((p, i) => (
                            <Reveal key={p.step} delay={i * 0.1}>
                                <div data-testid={`process-step-${i}`} className="card-glow-border h-full rounded-2xl border border-white/[0.08] bg-card2/50 p-7">
                                    <div className="font-syne text-3xl font-extrabold text-volt/80">{p.step}</div>
                                    <h3 className="mb-2 mt-3 font-syne text-lg font-semibold text-white">{p.title}</h3>
                                    <p className="text-sm leading-relaxed text-slate-400">{p.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
